import { Plus } from "@phosphor-icons/react";
import { useInitiateMonoLink } from "../../../hooks/useInitiateMonoLink";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "sonner";
import useLinkedAccounts from "../../../hooks/useLinkedAccounts";
import { getBankMeta } from "../../../helpers/bankData";
import { useEffect } from "react"; 
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/* ----------------------------- COMPONENT ----------------------------- */
const BankAccounts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;

  const { accounts , refetch} = useLinkedAccounts();
  

  const { initiateMonoLink, initiatingMonoLink } = useInitiateMonoLink();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {

    const status = searchParams.get("status"); 
    
    if (status === "successful" || status === "success") {
      toast.success("Account linked successfully!");
      refetch(); 
      
      setSearchParams({}); 
      
      const isFromSetup = localStorage.getItem("mono_from_setup") === "true";
      if (isFromSetup) {
        localStorage.removeItem("mono_from_setup");
        navigate("/home");
      }
    }
  }, [searchParams, refetch, setSearchParams, navigate]);

  const handleLinkNewAccount = () => {
    if (user) {
      if (fromSetup) {
        localStorage.setItem("mono_from_setup", "true");
      }
      initiateMonoLink(
        {
          name: `${user?.firstname} ${user?.lastname}`,
          email: user?.email || "",
          ref: user?.id,
          redirect_url: window.location.href,
        },
        {
          onSuccess: (data) => {
            if (data.data?.mono_url) {
              window.location.href = data.data?.mono_url;
            }
          },
          onError: (err) => {
            toast.error(err.message);
          },
        }
      );
    }
  };

  const isEmpty = accounts?.data?.length === 0;

  return (
    <div className="px-4 sm:px-6 md:px-10 py-8 flex flex-col gap-10 w-full">
      <div className="relative w-full flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-center sm:text-left">
            <h3 className="text-xl sm:text-2xl font-semibold">
              Your Linked Bank Account
            </h3>
            <p className="text-gray-500 text-sm mt-1 max-w-lg">
              Linked bank accounts are used to generate your bank statements for
              loan approval and financial assessment.
            </p>
          </div>

          <button
            disabled={initiatingMonoLink}
            onClick={handleLinkNewAccount}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm sm:text-base bg-deepBlue text-white hover:bg-deepBlue/90 transition-all disabled:opacity-70 w-full sm:w-auto"
          >
            <Plus size={18} />
            {initiatingMonoLink ? "Connecting..." : "Link New Account"}
          </button>
        </div>

        {/* Accounts List or Empty State */}
        {!isEmpty ? (
          <div className="flex flex-col gap-4">
            {accounts?.data?.map((account) => {
              const bankMeta = getBankMeta(account.institution.name);
              return (
                <div
                  key={account.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-gray-400 transition-all duration-300 bg-white"
                >
                  <div className="flex-shrink-0">
                    <img
                      src={bankMeta?.imageUrl ?? "/images/default-bank.png"}
                      alt={bankMeta?.alt ?? account.institution.name}
                      className="w-12 h-12 object-contain mx-auto sm:mx-0"
                    />
                  </div>

                  <div className="flex flex-col flex-1 text-center sm:text-left">
                    <span className="text-base sm:text-lg font-semibold">
                      {account.institution.name}
                    </span>
                    <span className="text-gray-600 text-sm">
                      {account.name} • {account.account_number}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-400 mt-1">
                      {account.type.replaceAll("_", " ")} • {account.status}
                    </span>
                  </div>

                  <div className="text-center sm:text-right">
                    <span className="font-medium text-base sm:text-lg">
                      ₦{account.balance.toLocaleString()}
                    </span>
                    <div className="text-xs sm:text-sm text-gray-500 mt-1">
                      {account.currency}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-16 px-4">
            <div className="text-gray-500 mb-4 text-sm sm:text-base">
              You don’t have any linked bank accounts yet.
            </div>
            <button
              onClick={handleLinkNewAccount}
              disabled={initiatingMonoLink}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm sm:text-base bg-deepBlue text-white hover:bg-deepBlue/90 transition-all disabled:opacity-70"
            >
              <Plus size={18} />
              {initiatingMonoLink ? "Connecting..." : "Link New Account"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankAccounts;
